import { translate } from "app/language/service"
import { APP_CONTEXTS } from "app/model"
import { getStore } from "app/store/store"
import { refreshInventory } from "form/inventory/tools/common"
import { IForm, IFormDataLoader, SELECT_FILTER_VALUES } from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getForm } from "form/tools/common"
import { messageSVC } from "message/service"
import { normalizeSync } from "normalize-diacritics"
import { IOperator, IOperatorId, IOperatorSetFilterParams } from "operator/model"
import { operatorRPO } from "operator/repository"
import { getStringOperatorId } from "operator/tools/common"
import { getAllSspsInStore } from "ssp/tools/common"
import { refreshOperatorForecast } from "./operator-forecast"
import { getScreensChanges } from "./screens"

export async function refreshOperatorList(appContext: APP_CONTEXTS, sspIds?: number[]): Promise<void> {
  if (!sspIds) {
    sspIds = getAllSspsInStore().map(s => s.id)
  }
  state.operatorRunId++
  if (sspIds.length === 0) {
    getStore().dispatch(formActionCreator.setAllOperators(appContext, []))
    getStore().dispatch(formActionCreator.setOperatorsForecast(appContext, {}))
    return
  }
  if (await refreshAllOperators(appContext, sspIds, state.operatorRunId)) {
    refreshOperatorForecast(state.operatorRunId)
  }
}

export async function refreshAllOperators(appContext: APP_CONTEXTS, sspIds: number[], runId: number): Promise<boolean> {
  try {
    getStore().dispatch(formActionCreator.startLoadingAllOperators(appContext))
    const allOperators = await operatorRPO.getAllOperators(sspIds)
    if (runId === state.operatorRunId) {
      getStore().dispatch(formActionCreator.setAllOperators(appContext, allOperators))
    }
    return true
  } catch (error) {
    console.error("Unable to refresh all operators", error)
    if (runId === state.operatorRunId) {
      getStore().dispatch(formActionCreator.setHasLoadingAllOpertorsError(appContext, true))
    }
    return false
  }
}

export const operatorFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const operators = await operatorRPO.getCampaignOperators(form.tempCampaignId)
    form.context.relations.operators = operators
    form.screens.operators = operators.slice()
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Operator form data-loader error ${String(error)}`)
  }
}

export function synchronizeNewOperatorSelection(newSelectedOperators: string[] | IOperator[]): void {
  const form = getForm()
  getStore().dispatch(formActionCreator.startSynchronizingOperator(getForm().context.appContext))
  clearTimeout(state.operatorTimeout)
  if (newSelectedOperators.length > 0 && typeof newSelectedOperators[0] === "string") {
    newSelectedOperators = form.screens.allOperators.filter(o =>
      newSelectedOperators.includes(getStringOperatorId(o) as any)
    )
  }
  state.operatorTimeout = setTimeout(() => setOperators(newSelectedOperators as IOperator[]), 800)
}

export function getFilteredOperators(
  allOperators: IOperator[],
  nameFilter: string,
  selectFilter: SELECT_FILTER_VALUES,
  selectedOperatorsIds: IOperatorId[]
): IOperator[] {
  if (!nameFilter && selectFilter === SELECT_FILTER_VALUES.ALL) {
    return allOperators
  }
  const stringOperatorIds = selectedOperatorsIds.map(id => getStringOperatorId(id))
  const filteredOperators: IOperator[] = []
  const preparedNameFilter = normalizeSync(nameFilter).toLowerCase()
  for (const operator of allOperators) {
    if (nameFilter && normalizeSync(operator.publisher_name).toLowerCase().indexOf(preparedNameFilter) === -1) {
      continue
    }
    if (selectFilter !== SELECT_FILTER_VALUES.ALL) {
      const isSelected = stringOperatorIds.includes(getStringOperatorId(operator))
      if (selectFilter === SELECT_FILTER_VALUES.SELECTED) {
        if (!isSelected) {
          continue
        }
      } else if (isSelected) {
        continue
      }
    }
    filteredOperators.push(operator)
  }
  return filteredOperators
}

export function setOperatorFilters(params: IOperatorSetFilterParams): void {
  getStore().dispatch(formActionCreator.setOperatorFilter(getForm().context.appContext, params))
}

export function noticeAndClearOperatorError(form: IForm): void {
  messageSVC.error(translate("operator.error.selection"))
  getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { operatorsError: false }))
}

// @recursive
async function setOperators(newSelectedOperators: IOperator[]): Promise<void> {
  if (state.operatorIsSynchronizing) {
    state.operatorToApplySelection = newSelectedOperators
    return
  } else {
    state.operatorIsSynchronizing = true
  }
  const form = getForm()
  let hasError = false
  try {
    const newOperatorAssociations = await operatorRPO.setOperators(form.tempCampaignId, newSelectedOperators)
    getStore().dispatch(
      formActionCreator.setFormOperators(
        form.context.appContext,
        getScreensChanges({
          ...form,
          screens: {
            ...form.screens,
            operators: newOperatorAssociations
          }
        }),
        newOperatorAssociations
      )
    )
  } catch (error) {
    console.error(error)
    hasError = true
    getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { operatorsError: hasError }))
    getStore().dispatch(formActionCreator.stopSynchronizingOperator(form.context.appContext))
  }
  state.operatorIsSynchronizing = false
  if (state.operatorToApplySelection) {
    const toApplyOperatorSelection = state.operatorToApplySelection
    state.operatorToApplySelection = null
    // @recursivity
    setOperators(toApplyOperatorSelection)
  } else if (!hasError) {
    refreshInventory()
  }
}
